import { Type, Image as ImageIcon, Music, Columns3 } from 'lucide-react';
import QuestionBlockEditor from './QuestionBlockEditor';
import ContainerBlockEditor from './ContainerBlockEditor';
import { genBlockId } from '../../lib/questionForm';
import type { QuestionBlock } from '@eps/shared-types';

const ORANGE = '#FF8C42';
const DEFAULT_COLUMNS = 1;

interface QuestionStemFieldsProps {
  blocks: QuestionBlock[];
  onBlocksChange: (blocks: QuestionBlock[]) => void;
  /** true when ubtLayout === 'number_only' — hides the "+ Text" chip; the group prompt supplies the text instead. */
  textDisabled?: boolean;
}

const ADD_CHIPS = [
  { type: 'text' as const, label: 'Text', icon: Type },
  { type: 'image' as const, label: 'Image', icon: ImageIcon },
  { type: 'audio' as const, label: 'Audio', icon: Music },
  { type: 'container' as const, label: 'Container', icon: Columns3 },
];

function newBlock(type: QuestionBlock['type']): QuestionBlock {
  switch (type) {
    case 'text':
      return { id: genBlockId(), type: 'text', text: '' };
    case 'image':
      return { id: genBlockId(), type: 'image', imageUrl: '' };
    case 'audio':
      return { id: genBlockId(), type: 'audio', audioUrl: '' };
    case 'container':
      return {
        id: genBlockId(),
        type: 'container',
        columns: Array.from({ length: DEFAULT_COLUMNS }, () => ({ blocks: [] })),
      };
  }
}

export default function QuestionStemFields({
  blocks,
  onBlocksChange,
  textDisabled,
}: QuestionStemFieldsProps) {
  const addBlock = (type: QuestionBlock['type']) => onBlocksChange([...blocks, newBlock(type)]);
  const updateBlock = (idx: number, updated: QuestionBlock) =>
    onBlocksChange(blocks.map((b, i) => (i === idx ? updated : b)));
  const removeBlock = (idx: number) => onBlocksChange(blocks.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {ADD_CHIPS.filter((c) => !(c.type === 'text' && textDisabled)).map(
          ({ type, label, icon: Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => addBlock(type)}
              className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors"
              style={{ borderColor: ORANGE, color: ORANGE }}
            >
              <Icon size={12} />+ {label}
            </button>
          ),
        )}
      </div>

      <div className="space-y-2">
        {blocks.map((block, idx) =>
          block.type === 'container' ? (
            <ContainerBlockEditor
              key={block.id}
              block={block}
              onChange={(updated) => updateBlock(idx, updated)}
              onDelete={() => removeBlock(idx)}
            />
          ) : (
            <QuestionBlockEditor
              key={block.id}
              block={block}
              onChange={(updated) => updateBlock(idx, updated)}
              onDelete={() => removeBlock(idx)}
            />
          ),
        )}
      </div>
    </div>
  );
}
